import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdminCustomerRow,
  type AdminSessionResponse,
  acquisitionChannelSchema,
} from "@ternary/contracts"
import {
  Avatar,
  Badge,
  Button,
  breakable,
  type Column,
  EmptyState,
  Field,
  FilterBar,
  Input,
  Menu,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  type SortState,
  Table,
  useToast,
} from "@ternary/ui"
import { type ChangeEvent, type ReactNode, useState } from "react"
import { Link, useLocation, useSearchParams } from "react-router-dom"
import {
  AdminApiError,
  checkMembersNow,
  downloadAdminCustomers,
  loadAdminCustomerFacets,
  loadAdminCustomers,
} from "../api.js"
import { ChannelBadge, channelLabel } from "../components/channel-badge.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { checkedLabel } from "../components/member-tracking-card.js"
import { NavIcon } from "../components/nav-icons.js"
import { SignupLinkAction } from "../components/signup-link-action.js"
import { type CustomerRecordState, discordAvatarSrc, fullName } from "../customer.js"
import {
  activeFilterCount,
  customerQuery,
  FILTER_KEYS,
  formatRange,
  parseCustomerFilters,
  withMemberDefault,
} from "../customer-filters.js"
import { shouldRetryAdminQuery } from "../query-client.js"

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" })
const COUNT = new Intl.NumberFormat("en-US")
/** The API searches the first five words and ignores the rest. */
const SEARCH_TERMS = 5
/** The API refuses a longer search. */
const SEARCH_LENGTH = 200

/** A page already shown: Previous returns to its cursor, and later pages skip its rows. */
type ShownPage = { cursor: string | null; ids: string[] }
type Paging = { key: string; cursor: string | null; previous: ShownPage[] }

/** The record's link. It carries the list's query in history state, for the record's back link. */
function RecordLink({ row }: { row: AdminCustomerRow }) {
  const { search } = useLocation()
  return (
    <Link
      state={{ customerList: search } satisfies CustomerRecordState}
      to={`/dashboard/customers/${row.id}`}
    >
      {fullName(row)}
    </Link>
  )
}

function Day({ iso }: { iso: string }) {
  return (
    <time className="admin-nowrap" dateTime={iso}>
      {DATE.format(new Date(iso))}
    </time>
  )
}

function muted(text: ReactNode) {
  return <span className="admin-note">{text}</span>
}

/** Name and email share the first column; each product the customer signed up for is a badge. */
const COLUMNS: Column<AdminCustomerRow>[] = [
  {
    cell: (row) => (
      <span className="admin-customer">
        <Avatar name={fullName(row)} size="sm" src={discordAvatarSrc(row.discordAvatarUrl)} />
        <span className="admin-customer-lines">
          <RecordLink row={row} />
          {muted(breakable(row.email))}
        </span>
      </span>
    ),
    header: "Customer",
    key: "name",
    sortable: true,
  },
  {
    cell: (row) => (row.channel ? <ChannelBadge channel={row.channel} /> : muted("Unknown")),
    header: "Channel",
    key: "channel",
  },
  {
    cell: (row) => {
      const name = row.discordUsername ? `@${row.discordUsername}` : muted("Not linked")
      // Member tracking only: a member who left the server is marked, never removed.
      return row.inServer === false && row.leftServerAt ? (
        <span className="admin-badges">
          {name}
          <Badge tone="warning">Left</Badge>
        </span>
      ) : (
        name
      )
    },
    header: "Discord",
    key: "discord",
    priority: 2,
  },
  {
    cell: (row) =>
      row.source || row.campaign ? (
        <span className="admin-customer-lines">
          {row.source ?? muted("No source")}
          {row.campaign ? muted(row.campaign) : muted("No campaign")}
        </span>
      ) : (
        muted("None")
      ),
    header: "Source / Campaign",
    key: "source",
    priority: 3,
  },
  {
    cell: (row) =>
      row.products.length > 0 ? (
        <span className="admin-badges">
          {row.products.map((product) => (
            <Badge key={product.id}>{product.name}</Badge>
          ))}
        </span>
      ) : (
        muted("None")
      ),
    header: "Products",
    key: "products",
    priority: 3,
  },
  {
    align: "end",
    cell: (row) => <Day iso={row.signedUpAt} />,
    header: "Signed up",
    key: "signedUpAt",
    priority: 2,
    sortable: true,
  },
  {
    align: "end",
    cell: (row) => (row.joinedAt ? <Day iso={row.joinedAt} /> : <Badge>Not yet</Badge>),
    header: "Joined",
    key: "joinedAt",
    priority: 2,
    sortable: true,
  },
]

/**
 * What a failed export toasts. A refusal (403) says why; a 503 (the audit or the read failed) and
 * a network failure, whose message is the browser's "Failed to fetch", say what to do next.
 */
function exportErrorText(error: Error) {
  if (!(error instanceof AdminApiError)) {
    return "The export could not reach the server. Check your connection, then try again."
  }
  return error.status === 503
    ? "Customers could not be exported right now. Try again in a minute."
    : error.message
}

/** A select's choices, plus the value in the URL when the facets do not list it (yet). */
function withCurrent(choices: string[], current: string | null) {
  return current && !choices.includes(current) ? [...choices, current] : choices
}

/**
 * The CRM list: who each person is and where they came from. The search, the filters and the sort
 * live in the URL; the page's cursor is a client-side stack that resets when they change.
 */
export function AdminCustomersPage() {
  const { toast } = useToast()
  const [params, setParams] = useSearchParams()
  const chosen = parseCustomerFilters(params)
  // Member tracking: a business that tracks lists the members in its server by default. The session
  // the shell loaded says so before the first list request; the list (`members`) says it too, so
  // a change since the session was read still applies. Every other business's query is today's.
  const queryClient = useQueryClient()
  const [tracking, setTracking] = useState(() => {
    const session = queryClient.getQueryData<AdminSessionResponse>(["admin-session"])
    return session?.authenticated === true && session.memberTracking === true
  })
  const filters = withMemberDefault(chosen, tracking)
  const filterCount = activeFilterCount(chosen)
  const filtered = filterCount > 0 || filters.search !== undefined

  // A change of filters or sort starts again at the first page.
  const key = customerQuery(filters, null).toString()
  const [paging, setPaging] = useState<Paging>({ cursor: null, key, previous: [] })
  const current: Paging = paging.key === key ? paging : { cursor: null, key, previous: [] }
  if (paging.key !== key) setPaging(current)

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => loadAdminCustomers(filters, current.cursor, { signal }),
    queryKey: ["admin-customers", key, current.cursor],
    // A 503 is a failed read or a search past the API's 5 s limit: retrying at once would make
    // the admin wait twice. Retry is theirs.
    retry: (count, error) =>
      !(error instanceof AdminApiError && error.status === 503) &&
      shouldRetryAdminQuery(count, error),
  })
  const facets = useQuery({
    queryFn: ({ signal }) => loadAdminCustomerFacets({ signal }),
    queryKey: ["admin-customer-facets"],
  })
  // The page's own data, never the previous page's while the next one loads.
  const list = query.isPlaceholderData ? undefined : query.data
  // A business that tracks members answers with `members`: its default filter then applies, and the
  // list loads again with it before any row shows.
  const members = list?.members ?? null
  if (list && Boolean(members) !== tracking) setTracking(Boolean(members))
  const check = useMutation({
    mutationFn: checkMembersNow,
    onError: (error) =>
      toast({
        message:
          error instanceof AdminApiError
            ? error.message
            : "Members could not be checked. Check your connection, then try again.",
        tone: "error",
      }),
    onSuccess: () =>
      toast({ message: "Checking members. It takes a minute or two.", tone: "success" }),
  })
  // The file holds every customer the filters match: the list's total when the export started.
  const exportFile = useMutation({
    mutationFn: async ({ format = "csv", total }: { format?: "csv" | "xlsx"; total: number }) => {
      await downloadAdminCustomers(filters, format)
      return total
    },
    onError: (error) => toast({ message: exportErrorText(error), tone: "error" }),
    onSuccess: (total) =>
      toast({
        message: `Downloaded ${COUNT.format(total)} ${total === 1 ? "customer" : "customers"}.`,
        tone: "success",
      }),
  })

  // Offset paging: a signup between two loads pushes rows already shown onto the next page.
  const seen = new Set(current.previous.flatMap((page) => page.ids))
  const rows = list ? list.data.filter((row) => !seen.has(row.id)) : []
  const first = current.previous.reduce((count, page) => count + page.ids.length, 0) + 1

  /** A search replaces its history entry, so Back skips the pauses in typing; a filter adds one. */
  function setParam(name: string, value: string, replace = false) {
    setParams(
      (before) => {
        const next = new URLSearchParams(before)
        if (value) next.set(name, value)
        else next.delete(name)
        return next
      },
      { replace },
    )
  }

  function clear(names: readonly string[]) {
    setParams((before) => {
      const next = new URLSearchParams(before)
      for (const name of names) next.delete(name)
      return next
    })
  }

  const change =
    (name: (typeof FILTER_KEYS)[number]) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setParam(name, event.currentTarget.value)

  function sortBy({ key: column, direction }: SortState) {
    const isDefault = column === "signedUpAt" && direction === "desc"
    setParams((before) => {
      const next = new URLSearchParams(before)
      if (isDefault) {
        next.delete("sort")
        next.delete("dir")
      } else {
        next.set("sort", column)
        next.set("dir", direction)
      }
      return next
    })
  }

  const clearLabel =
    filters.search === undefined
      ? "Clear filters"
      : filterCount > 0
        ? "Clear search and filters"
        : "Clear search"
  let empty = <p className="admin-note">No more customers.</p>
  if (list?.total === 0 && tracking && !filtered && filters.inServer === "yes") {
    // The default filter, not one the admin chose: nobody who signed up is in the server yet.
    empty = (
      <EmptyState
        action={
          <Button onClick={() => setParam("inServer", "all")} variant="secondary">
            Show all customers
          </Button>
        }
        description="Customers who left the server, or never joined it, are under All customers."
        title="No customers in the server"
      />
    )
  } else if (list?.total === 0) {
    empty = filtered ? (
      <EmptyState
        action={
          <Button onClick={() => clear([...FILTER_KEYS, "search"])} variant="secondary">
            {clearLabel}
          </Button>
        }
        description="Try other words, or fewer filters."
        title="No customers match"
      />
    ) : (
      <EmptyState
        action={<SignupLinkAction />}
        description="Share your signup link, and members appear here as they sign up."
        title="No customers yet"
      />
    )
  }

  // A 503 on a search is a read past the API's 5 s limit: a narrower search is the advice.
  const slowSearch =
    query.error instanceof AdminApiError && query.error.status === 503 && Boolean(filters.search)

  let description: string | undefined
  if (list) {
    const count = COUNT.format(list.total)
    description = filtered
      ? `${count} ${list.total === 1 ? "customer matches" : "customers match"}.`
      : `${count} ${list.total === 1 ? "customer has" : "customers have"} signed up.`
  }

  const productId = params.get("productId")
  const productNames = new Map(facets.data?.products.map((product) => [product.id, product.name]))
  const productChoices = withCurrent([...productNames.keys()], productId)
  const sourceChoices = withCurrent(facets.data?.sources ?? [], params.get("source"))
  const campaignChoices = withCurrent(facets.data?.campaigns ?? [], params.get("campaign"))
  const range = formatRange(filters.from, filters.to)
  const terms = filters.search?.split(/\s+/).length ?? 0

  return (
    <section className="admin-stack admin-page">
      <PageHeader
        actions={
          query.data?.canExport ? (
            members ? (
              // Member tracking's deployments also export a workbook, which keeps Discord ids as
              // text in Excel. Nothing to export disables both, as the button below.
              <Menu
                id="customers-export"
                items={(["csv", "xlsx"] as const).map((format) => ({
                  disabled: !list || list.total === 0 || exportFile.isPending,
                  label: format === "csv" ? "CSV" : "Excel (.xlsx)",
                  onSelect: () => list && exportFile.mutate({ format, total: list.total }),
                }))}
                label="Export"
              />
            ) : (
              <Button
                // Nothing to export, or the rows on screen are the previous filters' while the new
                // ones load: their total is not what the file would hold.
                disabled={!list || list.total === 0}
                loading={exportFile.isPending}
                onClick={() => list && exportFile.mutate({ total: list.total })}
                variant="secondary"
              >
                <NavIcon name="download" />
                Export CSV
              </Button>
            )
          ) : null
        }
        description={description}
        title="Customers"
      />
      {members ? (
        <div className="admin-row">
          <p className="admin-note" role="status">
            {checkedLabel(members.checkedAt, new Date())}
          </p>
          {query.data?.canExport ? (
            <Button
              loading={check.isPending}
              onClick={() => check.mutate()}
              size="sm"
              variant="secondary"
            >
              Check now
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="admin-customers-search">
        <SearchInput
          label="Search customers"
          maxLength={SEARCH_LENGTH}
          onSearch={(value) => setParam("search", value.trim(), true)}
          placeholder="Name, email or Discord"
          value={filters.search ?? ""}
        />
        {terms > SEARCH_TERMS ? (
          <p className="admin-note">Only the first {SEARCH_TERMS} words are searched.</p>
        ) : null}
      </div>
      <FilterBar
        activeCount={filterCount}
        label="Filter customers"
        onClear={() => clear(FILTER_KEYS)}
      >
        <Field label="Channel">
          <Select onChange={change("channel")} value={params.get("channel") ?? ""}>
            <option value="">All channels</option>
            {acquisitionChannelSchema.options.map((channel) => (
              <option key={channel} value={channel}>
                {channelLabel(channel)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Source">
          <Select onChange={change("source")} value={params.get("source") ?? ""}>
            <option value="">All sources</option>
            {sourceChoices.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Campaign">
          <Select onChange={change("campaign")} value={params.get("campaign") ?? ""}>
            <option value="">All campaigns</option>
            {campaignChoices.map((campaign) => (
              <option key={campaign} value={campaign}>
                {campaign}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Product">
          <Select onChange={change("productId")} value={productId ?? ""}>
            <option value="">All products</option>
            {productChoices.map((id) => (
              <option key={id} value={id}>
                {productNames.get(id) ?? id}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Joined">
          <Select onChange={change("joined")} value={params.get("joined") ?? ""}>
            <option value="">All</option>
            <option value="yes">Joined</option>
            <option value="no">Not yet</option>
          </Select>
        </Field>
        {tracking ? (
          <Field label="In the server">
            <Select onChange={change("inServer")} value={filters.inServer ?? "yes"}>
              <option value="yes">In the server</option>
              <option value="no">Not in the server</option>
              <option value="all">All customers</option>
            </Select>
          </Field>
        ) : null}
        {/* The typed dates, not the parsed ones: a year typed digit by digit passes through
            earlier years, which the From-after-To swap must not move to the other field. */}
        <Field label="From">
          <Input onChange={change("from")} type="date" value={params.get("from") ?? ""} />
        </Field>
        <Field label="To">
          <Input onChange={change("to")} type="date" value={params.get("to") ?? ""} />
        </Field>
        {/* A date input shows the browser's own format: the range reads back in the list's. */}
        {range ? <p className="admin-note admin-filter-range">{range}</p> : null}
      </FilterBar>
      <Table
        caption="Customers"
        captionHidden
        columns={COLUMNS}
        empty={empty}
        error={
          query.isError ? (
            // The table's slot is the alert.
            <LoadError
              description={slowSearch ? "Narrow your search, then try again." : undefined}
              detail={errorDetail(query.error)}
              message={slowSearch ? "Search took too long" : "We couldn't load your customers"}
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
              role="none"
            />
          ) : undefined
        }
        loading={query.isPending || query.isPlaceholderData}
        loadingLabel="Loading customers…"
        onSortChange={sortBy}
        rowKey={(row) => row.id}
        rows={rows}
        // The API's default order: newest signup first.
        sort={{ direction: filters.dir ?? "desc", key: filters.sort ?? "signedUpAt" }}
      />
      {current.previous.length > 0 || query.data?.nextCursor ? (
        <Pagination
          hasNext={Boolean(list?.nextCursor)}
          hasPrevious={current.previous.length > 0}
          label="Customer pages"
          onNext={() => {
            if (!list?.nextCursor) return
            setPaging({
              cursor: list.nextCursor,
              key,
              previous: [
                ...current.previous,
                { cursor: current.cursor, ids: rows.map((r) => r.id) },
              ],
            })
          }}
          onPrevious={() => {
            const last = current.previous.at(-1)
            if (last)
              setPaging({ cursor: last.cursor, key, previous: current.previous.slice(0, -1) })
          }}
          summary={
            !list
              ? "Loading…"
              : rows.length === 0
                ? "No more customers"
                : `${COUNT.format(first)}–${COUNT.format(first + rows.length - 1)} of ${COUNT.format(list.total)}`
          }
        />
      ) : null}
    </section>
  )
}
